import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { MemberProfilePort } from '../member-profile/MemberProfilePort.js';
import { invalidateMemberProfile } from '../member-profile/useMemberProfile.js';
import type { AuthState, SessionPort } from './SessionPort.js';

export interface SessionCacheBoundary {
  readonly generation: number;
  /** Another account replaced this tab's account; content stays hidden until the reload. */
  readonly replaced: boolean;
}

export function reloadPage(): void {
  window.location.reload();
}

export function useSessionCacheCleanup(
  session: SessionPort,
  profile: MemberProfilePort,
  queries: QueryClient,
  reload: () => void = reloadPage,
): SessionCacheBoundary {
  const [generation, setGeneration] = useState(0);
  const [replaced, setReplaced] = useState(false);
  useEffect(() => {
    let previous = session.state;
    let account = isAccount(previous) ? previous : undefined;
    return session.subscribe((state) => {
      if (isAccount(previous) && !sameAccount(previous, state)) {
        invalidateMemberProfile(profile);
        queries.clear();
        // Existing query observers and local forms must drop the old account too.
        setGeneration((value) => value + 1);
      }
      if (isAccount(state)) {
        if (account !== undefined && !sameAccount(account, state)) {
          // Case, My Data and billing reads are cached per long-lived port outside
          // React Query, so only a reload drops every read of the previous account.
          setReplaced(true);
          reload();
        }
        account = state;
      }
      previous = state;
    });
  }, [session, profile, queries, reload]);
  return { generation, replaced };
}

function isAccount(state: AuthState): boolean {
  return state.kind === 'member' || state.kind === 'admin';
}

function sameAccount(previous: AuthState, next: AuthState): boolean {
  if (previous.kind === 'admin' && next.kind === 'admin') {
    return previous.email === next.email && previous.demo === next.demo;
  }
  if (previous.kind === 'member' && next.kind === 'member') {
    return (
      previous.email === next.email &&
      previous.memberNumber === next.memberNumber &&
      previous.demoMemberKey === next.demoMemberKey &&
      previous.demo === next.demo
    );
  }
  return false;
}
